import React, { useState, useEffect } from 'react';
import { Select, Form, Input, DatePicker } from 'antd';
import { ButtonCommon } from 'components/common';
import { CaretDown, Funnel, XCircle } from '@phosphor-icons/react';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { EInvoiceFilter } from 'types/invoice.type';

dayjs.extend(quarterOfYear);

export enum DatePreset {
  THIS_MONTH = 'THIS_MONTH',
  LAST_MONTH = 'LAST_MONTH',
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
  THIS_YEAR = 'THIS_YEAR',
  LAST_YEAR = 'LAST_YEAR',
  CUSTOM = 'CUSTOM',
}

const PRESETS = [
  { label: 'Tháng này', value: DatePreset.THIS_MONTH },
  { label: 'Tháng trước', value: DatePreset.LAST_MONTH },
  { label: 'Quý 1 năm nay', value: DatePreset.Q1 },
  { label: 'Quý 2 năm nay', value: DatePreset.Q2 },
  { label: 'Quý 3 năm nay', value: DatePreset.Q3 },
  { label: 'Quý 4 năm nay', value: DatePreset.Q4 },
  { label: 'Năm nay', value: DatePreset.THIS_YEAR },
  { label: 'Năm trước', value: DatePreset.LAST_YEAR },
  { label: 'Lựa chọn khác', value: DatePreset.CUSTOM },
];

const getRangeFromPreset = (preset: DatePreset): [Dayjs, Dayjs] | null => {
  const now = dayjs();
  switch (preset) {
    case DatePreset.THIS_MONTH:
      return [now.startOf('month'), now.endOf('month')];

    case DatePreset.LAST_MONTH: {
      const lastMonth = now.subtract(1, 'month');
      return [lastMonth.startOf('month'), lastMonth.endOf('month')];
    }

    case DatePreset.Q1:
      return [now.quarter(1).startOf('quarter'), now.quarter(1).endOf('quarter')];
    case DatePreset.Q2:
      return [now.quarter(2).startOf('quarter'), now.quarter(2).endOf('quarter')];
    case DatePreset.Q3:
      return [now.quarter(3).startOf('quarter'), now.quarter(3).endOf('quarter')];
    case DatePreset.Q4:
      return [now.quarter(4).startOf('quarter'), now.quarter(4).endOf('quarter')];

    case DatePreset.THIS_YEAR:
      return [now.startOf('year'), now.endOf('year')];

    case DatePreset.LAST_YEAR: {
      const lastYear = now.subtract(1, 'year');
      return [lastYear.startOf('year'), lastYear.endOf('year')];
    }

    default:
      return null;
  }
};

const { RangePicker } = DatePicker;

interface AccountingDateRangeProps {
  value?: [Dayjs, Dayjs] | null;
  onChange?: (dates: [Dayjs, Dayjs] | null) => void;
}

const AccountingDateRange: React.FC<AccountingDateRangeProps> = ({ value, onChange }) => {
  const [pickerValue, setPickerValue] = useState<Dayjs>(dayjs());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value && value[0]) {
      setPickerValue(value[0]);
    }
  }, [value]);

  const handlePresetClick = (presetValue: DatePreset) => {
    if (presetValue === DatePreset.CUSTOM) {
      setOpen(true);
      return;
    }

    const range = getRangeFromPreset(presetValue);
    if (range && onChange) {
      onChange(range);
      setPickerValue(range[0]);
      setOpen(false);
    }
  };

  return (
    <RangePicker
      open={open}
      value={value}
      pickerValue={pickerValue}
      onOpenChange={setOpen}
      onClick={() => setOpen(true)}
      onCalendarChange={(dates) => {
        if (dates?.[0]) setPickerValue(dates[0]);
      }}
      onChange={(dates) => {
        onChange?.(dates as [Dayjs, Dayjs]);
        if (dates?.[0]) setPickerValue(dates[0]);
      }}
      panelRender={(panel) => (
        <div className='flex'>
          <div className='w-[160px] border-r border-gray-100 p-2 space-y-1 bg-white'>
            {PRESETS.map((item) => (
              <div
                key={item.value}
                className='px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm rounded transition-colors text-gray-700 hover:text-blue-600'
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePresetClick(item.value);
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
          <div>{panel}</div>
        </div>
      )}
      className='w-full'
      size='large'
      format='DD/MM/YYYY'
    />
  );
};

interface FilterEInvoiceProps {
  initialFilter?: Partial<EInvoiceFilter>;
  onApplyFilter: (filter: EInvoiceFilter) => void;
  onClearFilter: () => void;
}

const FilterEInvoice: React.FC<FilterEInvoiceProps> = ({ initialFilter, onApplyFilter, onClearFilter }) => {
  const [form] = Form.useForm();

  const defaultFilter = {
    ...initialFilter,
    dateRange: initialFilter?.dateRange ?? [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
  };

  useEffect(() => {
    if (initialFilter) {
      form.setFieldsValue(initialFilter);
    }
  }, [initialFilter, form]);

  const handleClearFilter = () => {
    form.resetFields();
    onClearFilter();
  };

  const handleApplyFilter = () => {
    form.validateFields().then((values) => {
      onApplyFilter(values);
    });
  };

  return (
    <div className=' bg-white flex flex-col w-full '>
      <div className='flex-1 overflow-y-auto px-4 py-4 custom-scrollbar overflow-hidden'>
        <Form form={form} layout='vertical' initialValues={defaultFilter} className='filter-form'>
          <div className='flex flex-col gap-2'>
            <Form.Item
              label={<span className='text-[14px] font-medium text-gray-900'>Mã tham chiếu</span>}
              name='MaTraCuu'
            >
              <Input
                placeholder='Nhập mã tham chiếu'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                size='large'
              />
            </Form.Item>

            <Form.Item
              label={<span className='text-[14px] font-medium text-gray-900'>Thông tin khách hàng</span>}
              name='TTKhachHang'
            >
              <Input
                placeholder='Nhập thông tin khách hàng'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                size='large'
              />
            </Form.Item>

            <Form.Item
              label={<span className='text-[14px] font-medium text-gray-900'>Thời gian tạo hoá đơn điện tử</span>}
              name='dateRange'
            >
              <AccountingDateRange />
            </Form.Item>

            <Form.Item
              label={<span className='text-[14px] font-medium text-gray-900'>Trạng thái hoá đơn</span>}
              name='TThaiHDon'
            >
              <Select
                placeholder='Chọn trạng thái hoá đơn'
                allowClear={{ clearIcon: <XCircle weight='fill' size={18} /> }}
                suffixIcon={<CaretDown size={18} />}
                options={[
                  { value: -1, label: 'Tất cả ' },
                  { value: 1, label: 'Nháp' },
                  { value: 2, label: 'Lỗi ký' },
                  { value: 3, label: 'Chưa gửi CQT' },
                  { value: 4, label: 'Đã cấp mã/tiếp nhận' },
                  { value: 5, label: 'CQT chưa phản hồi' },
                  { value: 6, label: 'Không đủ điều kiện cấp mã' },
                ]}
                size='large'
              />
            </Form.Item>
          </div>
        </Form>
      </div>

      <div className='shrink-0 p-4 border-t border-gray-100 bg-white absolute bottom-0'>
        <div className='flex gap-4'>
          <ButtonCommon color='none' size='md' radius='rounded' className='min-w-[112px]' onClick={handleClearFilter}>
            Xóa bộ lọc
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='md'
            radius='rounded'
            fullWidth
            className='flex justify-center items-center gap-2'
            onClick={handleApplyFilter}
          >
            <Funnel size={16} />
            Áp dụng bộ lọc
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default FilterEInvoice;
